const { CError } = require("../../../consoleMixin")
import ClientStatusReasonMappings from '../../../../shared/ClientStatusReasonMappings.json'
/* eslint-disable max-len */

/**
 * @typedef {import('../../js-doc').UiClientModel} UiClientModel
 * @typedef {import('../../js-doc').V1ClientStatus} V1ClientStatus
 * @typedef {import('../../js-doc').V1UpdateClientRequest} V1UpdateClientRequest
 * @typedef {import('../../js-doc').V1ClientReserveRateReason} V1ClientReserveRateReason
 * @typedef {import('../../js-doc').V1ReserveRateHistoryResponse} V1ReserveRateHistoryResponse
 * @typedef {import('../../js-doc').V2ClientFactoringConfigResponse} V2ClientFactoringConfigResponse
 * @typedef {import('../../js-doc').V2UpdateClientFactoringConfigRequest} V2UpdateClientFactoringConfigRequest
 * @typedef {import('../../js-doc').V2ClientResponse} V2ClientResponse
 * @typedef {import('../../js-doc').V2MappingOptions} V2MappingOptions
 * @typedef {import('../../js-doc').V2ClientFactoringStatus} V2ClientFactoringStatus
 * @typedef {import('../../js-doc').V2ClientFactoringStatusUpdateReason} V2ClientFactoringStatusUpdateReason
 * @typedef {import('../../js-doc').V2ClientFactoringFactoringRateUpdateReason} V2ClientFactoringFactoringRateUpdateReason
 * @typedef {import('../../js-doc').V2ClientReserveRateStatus} V2ClientReserveRateStatus
 * @typedef {import('../../js-doc').V1ReserveTotalResponse} V1ReserveTotalResponse
 * @typedef {import('../../js-doc').V2ReserveTotalResponse} V2ReserveTotalResponse
 * @typedef {import('../../js-doc').V2ClientReserveRateHistory} V2ClientReserveRateHistory
 */

/**
 * @param {V2MappingOptions} v2MappingOptions - V2 mapping options
 * @param {V1UpdateClientRequest} v1Request - V1 create invoice request body
 * @returns {V2UpdateClientFactoringConfigRequest} - V2 create invoice request body
 */
export const mapV1UpdateClientRequestToV2 = (v2MappingOptions, v1Request) => {
  const status = v1Request.status
    ? mapClientStatus(v1Request.status)
    : undefined
  const statusReason = status
    ? getClientStatusUpdateReason(status, v1Request)
    : undefined

  return {
    __v1Payload: v1Request,
    __ingestThrough: v2MappingOptions.ingestThrough,
    factoringRatePercentage: v1Request.factor_rate_percentage,
    factoringRateReason: v1Request.factor_rate_change_reason
      ? mapClientFactorRateReason(v1Request.factor_rate_change_reason)
      : undefined,
    reserveRatePercentage: v1Request.reserve_rate_percentage,
    clientLimitAmount: v1Request.client_limit,
    clientLimitNote: v1Request.client_limit
      ? v1Request.changelog_notes || 'No notes'
      : undefined,
    reserveRateReason: v1Request.reserve_rate_change_reason
      ? mapClientReserveRateReason(v1Request.reserve_rate_change_reason)
      : undefined,
    note: v1Request.changelog_notes || '',
    statusReason,
    status,
    successTeamId: v1Request.account_manager_id,
    salesRepId: v1Request.sales_rep_id,
    vip: v1Request.vip_status,
    requiresVerification: v1Request.requires_verification,
    ccInEmails: v1Request.cc_in_emails,
    expediteTransferOnly: v1Request.expedite_flag,
    doneSubmittingInvoices: v1Request.done_submitting_invoices_flag,
    paymentPlan: v1Request.payment_plan_details,
    insuranceAgency: v1Request.insurance_agency,
    insuranceCompany: v1Request.insurance_company,
    insuranceMonthlyPaymentPerTruck: v1Request.insurance_monthly_payment_per_truck,
    insuranceRenewalDate: v1Request.insurance_renewal_date,
    ofacVerified: v1Request.ofac_verified,
    carrier411Alerts: v1Request.carrier_411_alerts,
    taxGuardAlerts: v1Request.tax_guard_alerts,
    dryvanTrucksAmount: v1Request.dryvan_trucks_amount,
    refrigeratedTrucksAmount: v1Request.refrigerated_trucks_amount,
    flatbedTrucksAmount: v1Request.flatbed_trucks_amount,
    stepdeckTrucksAmount: v1Request.stepdeck_trucks_amount,
    leasedTrucksAmount: v1Request.leased_trucks_amount,
    otherTrucksAmount: v1Request.other_trucks_amount,
    leadAttribution: mapLeadAttribution(v1Request.lead_attribution),
  }
}

/**
 * @param {V2ClientFactoringStatus} status - Client factoring status
 * @param {any} v1Payload - V1 client payload
 * @returns {V2ClientFactoringStatusUpdateReason}
 */
const getClientStatusUpdateReason = (status, v1Payload) => {
  if (status === 'hold' && v1Payload.status === "out of business") {
    return 'other'
  }
  if (status === 'hold') {
    return ClientStatusReasonMappings[v1Payload.on_hold_reason]
  }
  if (status === 'released') {
    return ClientStatusReasonMappings[v1Payload.release_reason]
  }
  return 'other'
}

/**
 * @param {string} v1FactorRateReason - V1 factor rate change reason
 * @returns {V2ClientFactoringFactoringRateUpdateReason}
 */
const mapClientFactorRateReason = (v1FactorRateReason) => {
  switch (v1FactorRateReason) {
    case 'rate correction':
      return 'rate_correction'
    case 'rate increase':
      return 'rate_increase'
    case 'lower rate request':
      return 'lower_rate_request'
    default:
      CError(`Client status ${v1FactorRateReason} can't be mapped from v1 to v2`)
      return 'rate_correction'
  }
}

/**
 * @param {V1ClientReserveRateReason} v1Reason - V1 reserve rate change reason
 * @returns {V2ClientReserveRateStatus}
 */
const mapClientReserveRateReason = (v1Reason) => {
  switch (v1Reason) {
    case 'dilution rate':
      return 'dilution_rate'
    case 'volume':
      return 'volume'
    case 'high risk brokers':
      return 'high_risk_brokers'
    case 'general risk':
      return 'general_risk'
    case 'other':
      return 'other'
    case 'none':
      return 'none'
    default:
      CError(`Client reserve rate ${v1Reason} can't be mapped from v1 to v2`)
      return 'other'
  }
}

const mapLeadAttribution = (v1LeadAttribution) => {
  switch (v1LeadAttribution) {
    case 'cold calling':
      return 'cold_calling'
    case 'existing customer':
      return 'existing_customer'
    case 'web':
      return 'web'
    case 'referral':
      return 'referral'
    case 'email':
      return 'email'
    default:
      CError(`Client lead attribution ${v1LeadAttribution} can't be mapped from v1 to v2`)
      return null
  }
}


/**
 * @param {V1ClientStatus=} v1Status - V1 status
 * @returns {V2ClientFactoringStatus}
 */
const mapClientStatus = (v1Status) => {
  switch (v1Status) {
    case 'on hold':
    case 'out of business':
      return 'hold'
    case 'pending':
      return 'onboarding'
    case 'active':
      return 'active'
    case 'released':
      return 'released'

    default:
      CError(`Client status ${v1Status} can't be mapped from v1 to v2`)
      return 'onboarding'
  }
}
/* eslint-enable max-len */
